'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Img } from '@/shared/components/ui/Img';
import { cn } from '@/shadcn/lib/utils';

import 'swiper/css';
import 'swiper/css/pagination';

const THUMB_WIDTH = 120;
const ROW_HEIGHT = 80;
const GAP = 6;

type Rows = 1 | 2;

interface Props {
    listing: {
        slug: string;
        id: string;
    };
    gallery: Array<{ src: string }>;
    multirows?: boolean;
    queryString?: string;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];

    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }

    return chunks;
}

function useSlidesPerView() {
    const ref = useRef<HTMLDivElement | null>(null);
    const [slidesPerView, setSlidesPerView] = useState(1);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver(([entry]) => {
            const width = entry.contentRect.width;
            const count = Math.floor((width + GAP) / (THUMB_WIDTH + GAP));

            setSlidesPerView(Math.max(1, count));
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    return { ref, slidesPerView };
}

function resolveRows(imageCount: number, multirows: boolean, slidesPerView: number): Rows {
    if (!multirows) {
        return 1;
    }

    const slidesWithTwoRows = Math.ceil(imageCount / 2);

    return slidesWithTwoRows > slidesPerView ? 2 : 1;
}

export function ListingGalleryThumbs(props: Props) {
    const { ref, slidesPerView } = useSlidesPerView();
    const [sliderInit, setSliderInit] = useState(false);

    const rows = resolveRows(props.gallery.length, props.multirows ?? false, slidesPerView);

    const columns = useMemo(() => {
        return chunkArray(props.gallery, rows);
    }, [props.gallery, rows]);

    const columnHeight = ROW_HEIGHT * rows + GAP * (rows - 1);
    const shouldLoop = columns.length > slidesPerView;

    const hrefFor = (idx: number) => {
        const qs = new URLSearchParams(props.queryString ?? '');

        qs.set('gallery', 'open');
        qs.set('galleryImage', String(idx));

        return `/inzerat/${props.listing.id}/${props.listing.slug}?${qs.toString()}`;
    };

    if (props.gallery.length === 0) {
        return null;
    }

    return (
        <div ref={ref} className="min-w-0 overflow-hidden">
            <Swiper
                modules={props.multirows ? [Autoplay, Pagination] : [Autoplay]}
                slidesPerView={slidesPerView}
                spaceBetween={GAP}
                loop={shouldLoop}
                pagination={
                    shouldLoop
                        ? {
                              clickable: true,
                              renderBullet: (_, className) =>
                                  `<span class="${className} realico-gallery-dot"></span>`,
                          }
                        : false
                }
                autoplay={
                    shouldLoop
                        ? {
                              delay: 3000,
                              disableOnInteraction: false,
                          }
                        : false
                }
                onInit={() => setSliderInit(true)}
                style={sliderInit ? { opacity: '1' } : { opacity: '0' }}
                className={cn(
                    props.multirows && 'lg:!pb-[24px]',

                    '[&_.swiper-pagination]:!bottom-0',
                    '[&_.swiper-pagination]:!flex',
                    '[&_.swiper-pagination]:!items-center',
                    '[&_.swiper-pagination]:!justify-center',
                    '[&_.swiper-pagination]:!gap-[6px]',

                    '[&_.realico-gallery-dot]:!m-0',
                    '[&_.realico-gallery-dot]:!block',
                    '[&_.realico-gallery-dot]:!h-[8px]',
                    '[&_.realico-gallery-dot]:!w-[8px]',
                    '[&_.realico-gallery-dot]:!rounded-full',
                    '[&_.realico-gallery-dot]:!bg-[#d4d4d8]',
                    '[&_.realico-gallery-dot]:!opacity-100',
                    '[&_.realico-gallery-dot]:!transition-all',
                    '[&_.realico-gallery-dot]:!duration-200',

                    '[&_.realico-gallery-dot.swiper-pagination-bullet-active]:!w-[20px]',
                    '[&_.realico-gallery-dot.swiper-pagination-bullet-active]:!bg-black',
                )}
            >
                {columns.map((column, columnIdx) => (
                    <SwiperSlide key={columnIdx} style={{ height: columnHeight }}>
                        <div
                            className="grid gap-[6px]"
                            style={{
                                gridTemplateRows: `repeat(${rows}, ${ROW_HEIGHT}px)`,
                            }}
                        >
                            {column.map((item, rowIdx) => {
                                const imageIdx = columnIdx * rows + rowIdx;

                                return (
                                    <Link
                                        key={imageIdx}
                                        href={hrefFor(imageIdx)}
                                        className="block overflow-hidden rounded-[8px]"
                                        style={{ height: ROW_HEIGHT }}
                                    >
                                        <Img
                                            src={item.src}
                                            className="block h-full w-full object-cover"
                                        />
                                    </Link>
                                );
                            })}
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
}
